import { z } from "zod";
import { pageOf, PageQuerySchema } from "../common/pagination.js";
import { IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
import { DisciplineSchema } from "../common/tunisia.js";
import { DocumentVersionStatusSchema } from "../documents/uploads.js";
import { RichTextDocSchema } from "../tasks/index.js";
const CHANNEL_KINDS = ["general", "discipline", "private", "direct", "group"];
const ChannelKindSchema = z.enum(CHANNEL_KINDS);
const CHANNEL_NAME_MAX = 80;
const CHANNEL_TOPIC_MAX = 250;
const CHANNEL_MEMBERS_MAX = 50;
const CHANNEL_PERMISSIONS = ["can_post", "moderator"];
const ChannelPermissionSchema = z.enum(CHANNEL_PERMISSIONS);
const ChannelParamsSchema = z.strictObject({ channelId: IdSchema });
const MessageParamsSchema = z.strictObject({ messageId: IdSchema });
const ChannelMemberSchema = z.strictObject({
  userId: IdSchema,
  displayName: z.string()
});
const ChannelSchema = z.strictObject({
  id: IdSchema,
  /** Null for organisation-wide, direct and group channels. */
  spaceId: IdSchema.nullable(),
  kind: ChannelKindSchema,
  /** Null for a direct chat: name it after the other person. */
  name: z.string().nullable(),
  discipline: DisciplineSchema.nullable(),
  topic: z.string().nullable(),
  /** Direct and group chats list their people; other channels leave it empty. */
  members: z.array(ChannelMemberSchema).max(CHANNEL_MEMBERS_MAX),
  /** Messages after the caller's last read one, not their own, not deleted. */
  unreadCount: z.int().min(0),
  /** When the last message (deleted ones excluded) was posted; null for an empty channel. */
  lastMessageAt: IsoDateTimeSchema.nullable(),
  archivedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  version: z.int().min(1),
  /** `moderator` is present when the caller may moderate; `can_post` unless archived. */
  _permissions: z.array(ChannelPermissionSchema)
});
const ChannelListSchema = pageOf(ChannelSchema);
const ChannelListQuerySchema = PageQuerySchema;
const ChannelNameSchema = z.string().trim().min(1).max(CHANNEL_NAME_MAX);
const ChannelTopicSchema = z.string().trim().min(1).max(CHANNEL_TOPIC_MAX);
const MemberIdsSchema = z.array(IdSchema).max(CHANNEL_MEMBERS_MAX - 1).refine((ids) => new Set(ids).size === ids.length, "people must be distinct");
const CreateSpaceChannelRequestSchema = z.strictObject({
  kind: z.enum(["general", "discipline", "private"]),
  name: ChannelNameSchema,
  discipline: DisciplineSchema.optional(),
  topic: ChannelTopicSchema.optional(),
  memberIds: MemberIdsSchema.default([])
}).superRefine((body, ctx) => {
  if (body.kind === "discipline" !== (body.discipline !== void 0))
    ctx.addIssue({
      code: "custom",
      path: ["discipline"],
      message: "a discipline channel names its discipline, other channels have none"
    });
  if (body.kind !== "private" && body.memberIds.length > 0)
    ctx.addIssue({
      code: "custom",
      path: ["memberIds"],
      message: "only a private channel has a list of members"
    });
});
const CreateOrgChannelRequestSchema = z.strictObject({
  kind: z.enum(["general", "direct", "group"]),
  name: ChannelNameSchema.optional(),
  topic: ChannelTopicSchema.optional(),
  memberIds: MemberIdsSchema.default([])
}).superRefine((body, ctx) => {
  if (body.kind === "direct" && body.memberIds.length !== 1)
    ctx.addIssue({
      code: "custom",
      path: ["memberIds"],
      message: "a direct chat is with exactly one other person"
    });
  if (body.kind === "direct" && (body.name !== void 0 || body.topic !== void 0))
    ctx.addIssue({
      code: "custom",
      path: ["name"],
      message: "a direct chat has no name or topic"
    });
  if (body.kind === "group" && body.memberIds.length < 1)
    ctx.addIssue({
      code: "custom",
      path: ["memberIds"],
      message: "a group chat needs at least one other person"
    });
  if (body.kind !== "direct" && body.name === void 0)
    ctx.addIssue({ code: "custom", path: ["name"], message: "name the channel" });
  if (body.kind === "general" && body.memberIds.length > 0)
    ctx.addIssue({
      code: "custom",
      path: ["memberIds"],
      message: "an organisation-wide channel is open to every member"
    });
});
const UpdateChannelRequestSchema = z.strictObject({
  name: ChannelNameSchema,
  topic: ChannelTopicSchema.nullable(),
  archived: z.boolean()
}).partial().refine((body) => Object.keys(body).length > 0, "send at least one field to change");
const MessageAuthorSchema = z.strictObject({
  id: IdSchema,
  displayName: z.string(),
  /** The organisation the author acted for. */
  organizationId: IdSchema,
  /** Null when the caller cannot see that organisation (a partner's, before COL). */
  organizationName: z.string().nullable(),
  /** The author acted for another organisation than the one that owns the channel. */
  external: z.boolean()
});
const REACTION_EMOJIS = ["\u{1F44D}", "\u2705", "\u2764\uFE0F", "\u{1F602}", "\u{1F62E}", "\u{1F64F}", "\u{1F440}", "\u{1F389}"];
const ReactionEmojiSchema = z.enum(REACTION_EMOJIS);
const REACTION_USERS_MAX = 200;
const ReactionSummarySchema = z.strictObject({
  emoji: ReactionEmojiSchema,
  count: z.int().min(1),
  userIds: z.array(IdSchema).max(REACTION_USERS_MAX)
});
const ReactionStateSchema = z.strictObject({
  messageId: IdSchema,
  reactions: z.array(ReactionSummarySchema).max(REACTION_EMOJIS.length)
});
const AddReactionRequestSchema = z.strictObject({ emoji: ReactionEmojiSchema });
const ReactionParamsSchema = z.strictObject({
  messageId: IdSchema,
  emoji: ReactionEmojiSchema
});
const MESSAGE_ATTACHMENTS_MAX = 10;
const AttachmentCardSchema = z.discriminatedUnion("access", [
  z.strictObject({
    access: z.literal("granted"),
    versionId: IdSchema,
    documentId: IdSchema,
    spaceId: IdSchema,
    title: z.string(),
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.int().min(0),
    /** `scanning` until the antivirus is done; `rejected` files are never offered. */
    status: DocumentVersionStatusSchema
  }),
  z.strictObject({ access: z.literal("none"), versionId: IdSchema })
]);
const MessageSchema = z.strictObject({
  id: IdSchema,
  channelId: IdSchema,
  /** The first message of the thread this one answers; null for a top-level message. */
  threadRootId: IdSchema.nullable(),
  author: MessageAuthorSchema,
  /** Empty for a tombstone. */
  body: RichTextDocSchema,
  /** People named in the body who can read the channel. */
  mentions: z.array(IdSchema),
  attachmentVersionIds: z.array(IdSchema).max(MESSAGE_ATTACHMENTS_MAX),
  /**
   * The attachments as the caller sees them, in the order of `attachmentVersionIds`. Present in
   * REST responses only (the cards depend on the reader); absent from realtime payloads.
   */
  attachments: z.array(AttachmentCardSchema).max(MESSAGE_ATTACHMENTS_MAX).optional(),
  reactions: z.array(ReactionSummarySchema).max(REACTION_EMOJIS.length),
  /** Pinned in its channel (false for a tombstone). */
  pinned: z.boolean(),
  /** Replies in the thread this message opens (deleted ones excluded). */
  replyCount: z.int().min(0),
  /** When the last reply (deleted ones excluded) was posted; null without replies. */
  lastReplyAt: IsoDateTimeSchema.nullable(),
  editedAt: IsoDateTimeSchema.nullable(),
  deletedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  version: z.int().min(1)
});
const MessageListSchema = pageOf(MessageSchema);
const MessageListQuerySchema = PageQuerySchema.extend({ threadRootId: IdSchema.optional() });
const CreateMessageRequestSchema = z.strictObject({
  body: RichTextDocSchema,
  threadRootId: IdSchema.optional(),
  attachmentVersionIds: z.array(IdSchema).max(MESSAGE_ATTACHMENTS_MAX).refine((ids) => new Set(ids).size === ids.length, "attach each file once").default([])
});
const UpdateMessageRequestSchema = z.strictObject({ body: RichTextDocSchema });
const MessageEditSchema = z.strictObject({
  id: IdSchema,
  editedBy: IdSchema,
  editedByName: z.string(),
  /** The text before this edit. */
  previousBodyText: z.string(),
  editedAt: IsoDateTimeSchema
});
const MessageEditListSchema = pageOf(MessageEditSchema);
const MarkChannelReadRequestSchema = z.strictObject({ lastReadMessageId: IdSchema });
const ChannelReadStateSchema = z.strictObject({
  channelId: IdSchema,
  lastReadMessageId: IdSchema,
  unreadCount: z.int().min(0)
});
const PINS_PER_CHANNEL_MAX = 50;
const PinMessageRequestSchema = z.strictObject({ messageId: IdSchema });
const PinParamsSchema = z.strictObject({ channelId: IdSchema, messageId: IdSchema });
const PinnedMessageSchema = z.strictObject({
  message: MessageSchema,
  pinnedBy: IdSchema,
  pinnedAt: IsoDateTimeSchema
});
const PinnedMessageListSchema = z.strictObject({
  items: z.array(PinnedMessageSchema).max(PINS_PER_CHANNEL_MAX)
});
const ChannelAttachmentsFolderSchema = z.strictObject({
  spaceId: IdSchema,
  folderId: IdSchema,
  /** The channel's folder name (the channel name made safe, made unique if needed). */
  name: z.string(),
  /**
   * The caller may add files to it. Public channels of a project space inherit the space: its
   * viewers read the files but only its contributors add some.
   */
  canUpload: z.boolean()
});
const UNREAD_CHANNELS_MAX = 200;
const UnreadSummarySchema = z.strictObject({
  total: z.int().min(0),
  channels: z.array(z.strictObject({ channelId: IdSchema, unreadCount: z.int().min(1) })).max(UNREAD_CHANNELS_MAX)
});
export {
  AddReactionRequestSchema,
  AttachmentCardSchema,
  CHANNEL_KINDS,
  CHANNEL_MEMBERS_MAX,
  CHANNEL_NAME_MAX,
  CHANNEL_PERMISSIONS,
  CHANNEL_TOPIC_MAX,
  ChannelAttachmentsFolderSchema,
  ChannelKindSchema,
  ChannelListQuerySchema,
  ChannelListSchema,
  ChannelMemberSchema,
  ChannelParamsSchema,
  ChannelPermissionSchema,
  ChannelReadStateSchema,
  ChannelSchema,
  CreateMessageRequestSchema,
  CreateOrgChannelRequestSchema,
  CreateSpaceChannelRequestSchema,
  MESSAGE_ATTACHMENTS_MAX,
  MarkChannelReadRequestSchema,
  MessageAuthorSchema,
  MessageEditListSchema,
  MessageEditSchema,
  MessageListQuerySchema,
  MessageListSchema,
  MessageParamsSchema,
  MessageSchema,
  PINS_PER_CHANNEL_MAX,
  PinMessageRequestSchema,
  PinParamsSchema,
  PinnedMessageListSchema,
  PinnedMessageSchema,
  REACTION_EMOJIS,
  REACTION_USERS_MAX,
  ReactionEmojiSchema,
  ReactionParamsSchema,
  ReactionStateSchema,
  ReactionSummarySchema,
  UNREAD_CHANNELS_MAX,
  UnreadSummarySchema,
  UpdateChannelRequestSchema,
  UpdateMessageRequestSchema
};
//# sourceMappingURL=index.js.map